import { Info } from "@phosphor-icons/react"
import { type CustomHeaderProps } from "ag-grid-react"
import { useCallback, useSyncExternalStore } from "react"
import { cn } from "~/utils/helpers"

interface HeaderCellProps extends CustomHeaderProps {
  subtitle: string
  textSize?: string
  align?: "left" | "right" | "center"
  infoIcon?: boolean
}

/**
 * The header text, kept current when a `headerValueGetter` reads grid props such as `context`.
 * AG Grid re-reads it only when it rebuilds the whole header row, which collapses an auto-height
 * header for a frame; ag-grid-react reports every changed grid prop as `componentStateChanged`.
 */
function useDisplayName({ api, column, displayName }: CustomHeaderProps) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      api.addEventListener("componentStateChanged", onChange)
      return () => {
        if (!api.isDestroyed()) api.removeEventListener("componentStateChanged", onChange)
      }
    },
    [api],
  )
  return useSyncExternalStore(subscribe, () =>
    column.getColDef().headerValueGetter
      ? (api.getDisplayNameForColumn(column, "header") ?? displayName)
      : displayName,
  )
}

/** Inner header for a column whose `headerValueGetter` reads grid props and that has no subtitle. */
export function HeaderName(props: CustomHeaderProps) {
  return useDisplayName(props)
}

export default function HeaderCell({
  subtitle,
  textSize,
  align = "left",
  infoIcon = false,
  ...props
}: HeaderCellProps) {
  const displayName = useDisplayName(props)
  return (
    <div
      className={cn(
        "flex flex-col gap-1 w-full max-h-[100px] text-[12px] leading-tight",
        align === "left" && "items-start text-left",
        align === "center" && "items-center text-center",
        align === "right" && "items-end text-right",
      )}
    >
      <div className="flex gap-1.5 text-fg-subtle items-center whitespace-normal break-words">
        <div className="whitespace-normal break-words">{displayName}</div>
        {infoIcon && <Info size={14} format="outline" weight="bold" />}
      </div>
      {subtitle && (
        <div
          className={cn(
            "font-normal text-fg-subtle whitespace-normal break-words",
            align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left",
            textSize ? `text-[${textSize}px]` : "",
          )}
        >
          {subtitle}
        </div>
      )}
    </div>
  )
}
